import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import AppointmentFormField from "components/appointment-form-field";
import DateFormField from "components/date-form-field";
import Dialog from "components/dialog";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { getInventoryPillMeta } from "components/inventory-product-field/inventory-product-field.util";
import InventoryProductPills from "components/inventory-product-field/inventory-product-pills.component";
import SelectFormField from "components/select-form-field";
import {
  PRODUCT_TYPES,
  SALE_CANCELED_STATUS_TYPE,
  SALE_DRAFT_STATUS_TYPE,
  SALE_QUOTE_STATUS_TYPE,
  SALE_STATUSES
} from "constants/sales";
import ProductCatalogField from "features/patients/sections/patient-hearing-aids/components/product-catalog-field";
import { ProductCatalogFieldProvider } from "features/patients/sections/patient-hearing-aids/components/product-catalog-field/use-product-catalog-field.hook";
import {
  useConvertQuoteSaleMutation,
  useGetQuoteConversionItemsQuery
} from "services/sales";
import type { TinyAppointment } from "services/types/appointments.type";
import type { InventoryProduct } from "services/types/inventory-products.type";
import type { SaleItem } from "services/types/sale-items.type";
import type { ConvertQuoteSaleItemInput } from "services/types/sales.type";
import { formatBackendDateOnly, formatDateOnlyValue } from "utils/common";

// Statuses a quote can be converted to (everything except Quote itself and
// Canceled — canceling a quote goes through the regular status change).
const CONVERSION_STATUS_OPTIONS = SALE_STATUSES.getOptions([
  SALE_QUOTE_STATUS_TYPE,
  SALE_CANCELED_STATUS_TYPE
]);

type QuoteConversionFormValues = {
  status: string;
  // Only `id` is read (onSubmit). The full TinyAppointment reaches ChartNote -> Patient and
  // Appointment, and tsc never finishes building react-hook-form's FieldPath union (ENG-2531).
  appointment: Pick<TinyAppointment, "id"> | null;
  serviceDate: string | null;
  // Dynamic per-device fields managed by ProductCatalogField:
  // devices.i{saleItemId} (catalog product, locked) and
  // devices.i{saleItemId}_from_stock (chosen stock unit, optional)
  devices: Record<string, any>;
};

// Does this line need a From Stock / Place Order decision?
//
// The backend owns this rule and answers it per line via
// `requires_sourcing_decision`, computed from the same predicate convert-quote
// validates the payload against. The rows we render therefore cannot drift from
// the decisions it accepts (SN-19383).
//
// Deliberately NOT reimplemented client-side. This used to guess from
// `product.master_product.type` — accepting every non-Service/non-Fee line — which
// offered a sourcing row for a SUPPLY "Wax Guards" line and made a practice's
// quote unconvertible: the POST carried a decision the backend rejected outright.
// If this ever looks wrong, fix `get_sale_item_sourcing_decision` in the backend,
// not this function.
export const isConvertibleQuoteItem = (item: SaleItem) =>
  item.requires_sourcing_decision === true;

const getItemFieldName = (item: SaleItem) => `devices.i${item.id}`;

// Full RHF path, like the `devices.i{id}_from_stock` key ProductCatalogField writes.
// Reads off the watched `devices` object use the bare `i{id}_exclude` key.
const getItemExcludeFieldName = (item: SaleItem) =>
  `devices.i${item.id}_exclude`;

// Turns the form's per-item state into the convert-quote payload.
//
// Exported and pure so the mapping can be tested without rendering the dialog: this is
// the whole contract with the backend, and every item that needs sourcing must appear
// exactly once with exactly one decision.
export const buildConversionItems = (
  items: SaleItem[],
  devices?: Record<string, any>
): ConvertQuoteSaleItemInput[] =>
  items.map((item) => {
    // Excluded items are still POSTed, with source "exclude". Dropping them from the
    // payload instead would trip the backend's missing-decision check — that guard is
    // what stops a real device being skipped by accident, so excluding is expressed as
    // a decision rather than an absence (SN-19383).
    //
    // Checked before the stock unit so a stale selection cannot outrank an exclusion.
    if (devices?.[`i${item.id}_exclude`]) {
      return { sale_item: Number(item.id), source: "exclude" };
    }

    const stockUnit = devices?.[`i${item.id}_from_stock`];
    return stockUnit
      ? {
          sale_item: Number(item.id),
          source: "stock",
          inventory_product: Number(stockUnit.id)
        }
      : {
          sale_item: Number(item.id),
          source: "order"
        };
  });

const getItemEar = (item: SaleItem): "L" | "R" | "" => {
  const ear = item.patient_products?.[0]?.inventory_product?.ear;
  return ear === "L" || ear === "R" ? ear : "";
};

// Spec pills for the QUOTED device, so the user can match the right stock unit.
// Wizard-added devices carry the captured spec on their placeholder inventory
// record; bare catalog items fall back to the product's master specification.
const getQuotedDevicePillMeta = (item: SaleItem) => {
  const quotedInventory =
    item.patient_products?.[0]?.inventory_product ?? item.inventory_product;
  return getInventoryPillMeta({
    ...(quotedInventory ?? {}),
    // The quote holds no physical unit yet — never show a serial pill
    serial_number: undefined,
    product: item.product
  } as unknown as InventoryProduct);
};

type QuoteConversionDialogProps = {
  open: boolean;
  onClose: () => void;
  saleId: number;
  patientId?: number | string;
  // Status the user picked from the status dropdown (defaults to Draft)
  initialStatus?: number;
  onConverted?: () => void;
};

// Shown at the moment a quote becomes a real sale: the user reviews the quoted
// items, decides per item whether to pull From Stock or place an order, and
// selects the appointment that sets the service date (insurance pays based on
// date of service, not date of quote). Sourcing uses the same From stock /
// catalog field pair as the new-hearing-aid wizard: picking a stock unit sells
// it, leaving it empty places an order for the (locked) quoted product, and a
// unit selected for one item is removed from the other items' stock lists.
// (SN-18215)
export default function QuoteConversionDialog({
  open,
  onClose,
  saleId,
  patientId,
  initialStatus = SALE_DRAFT_STATUS_TYPE,
  onConverted
}: QuoteConversionDialogProps) {
  const dispatch = useAppDispatch();

  // Dedicated read rather than the shared getSaleItems query: this is the only
  // surface that needs requires_sourcing_decision, and it is not free on the
  // backend. See QUOTE_CONVERSION_ITEMS_QUERY in services/sales.
  const { data: saleItems = [], isLoading: isSaleItemsLoading } =
    useGetQuoteConversionItemsQuery(
      { saleId: Number(saleId) },
      { skip: !open || !Number.isFinite(Number(saleId)) }
    );

  const [convertQuoteSale, { isLoading: isConverting }] =
    useConvertQuoteSaleMutation();

  const convertibleItems = useMemo(
    () => saleItems.filter(isConvertibleQuoteItem),
    [saleItems]
  );

  const { control, handleSubmit, setValue, watch, reset } =
    useForm<QuoteConversionFormValues>({
      mode: "onBlur",
      defaultValues: {
        status: initialStatus.toString(),
        appointment: null,
        serviceDate: null,
        devices: {}
      }
    });

  const status = watch("status");
  const serviceDate = watch("serviceDate");
  const devices = watch("devices");

  useEffect(() => {
    if (open) {
      reset({
        status: initialStatus.toString(),
        appointment: null,
        serviceDate: null,
        devices: {}
      });
    }
  }, [open, initialStatus, reset]);

  // Preset each device row with the quoted product — the catalog dropdown is
  // locked (the device is fixed by the quote), only its sourcing is chosen.
  useEffect(() => {
    convertibleItems.forEach((item) => {
      setValue(getItemFieldName(item) as any, (item.product as any) ?? null);
    });
  }, [convertibleItems, setValue]);

  const onAppointmentSelected = (appointment: TinyAppointment | null) => {
    // Auto-set the service date to the appointment date (critical for insurance
    // billing — insurance pays based on date of service).
    if (appointment?.startDate) {
      setValue("serviceDate", formatDateOnlyValue(appointment.startDate));
    }
  };

  const getStockSelection = (item: SaleItem) =>
    devices?.[`i${item.id}_from_stock`] ?? null;

  const isExcluded = (item: SaleItem) =>
    Boolean(devices?.[`i${item.id}_exclude`]);

  // Excluding and selecting a stock unit are contradictory instructions, so ticking
  // Exclude drops any chosen unit. Stock stays the primary choice: if the unit is on
  // the shelf we want it sold, not sourced outside Suno.
  const handleExcludeChange = (item: SaleItem, excluded: boolean) => {
    setValue(getItemExcludeFieldName(item) as any, excluded);
    if (excluded) {
      setValue(`devices.i${item.id}_from_stock` as any, null);
    }
  };

  // The other direction. The checkbox is hidden once a unit is picked, so it cannot be
  // ticked in that state — but ProductCatalogField writes the stock value itself and
  // gives no callback, so clear the flag here rather than trusting a render condition
  // to keep the two in sync. Guarantees the "excluded AND stocked" state simply cannot
  // exist, instead of relying on buildConversionItems' tie-break.
  useEffect(() => {
    convertibleItems.forEach((item) => {
      if (
        devices?.[`i${item.id}_from_stock`] &&
        devices?.[`i${item.id}_exclude`]
      ) {
        setValue(getItemExcludeFieldName(item) as any, false);
      }
    });
  }, [convertibleItems, devices, setValue]);

  const requiresServiceDate =
    Number(status) !== SALE_DRAFT_STATUS_TYPE && !serviceDate;

  const onSubmit = async (data: QuoteConversionFormValues) => {
    const items = buildConversionItems(convertibleItems, data.devices);

    try {
      await convertQuoteSale({
        id: Number(saleId),
        status: Number(data.status),
        appointment: data.appointment?.id ?? null,
        service_date: data.serviceDate
          ? formatBackendDateOnly(data.serviceDate, "yyyy-MM-dd")
          : null,
        items
      }).unwrap();

      dispatch(setCustomMessage("Quote converted to invoice"));
      dispatch(setSnackbarOpen(true));
      onConverted?.();
      onClose();
    } catch (error) {
      dispatch(setCustomMessage(<ErrorSnackbarContent response={{ error }} />));
      dispatch(setSnackbarOpen(true));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Convert Quote to Invoice"
      minWidth={640}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} mb={2}>
          <Typography variant="body2" color="text.secondary">
            This quote is becoming a real sale. Pick a stock unit for each item
            to sell it from stock, or leave &quot;From stock&quot; empty to
            place an order. Select the appointment for the service date.
          </Typography>

          <SelectFormField
            options={CONVERSION_STATUS_OPTIONS}
            name="status"
            label="New Invoice Status"
            control={control}
            required
          />

          {/* No type/status filter — mirrors the invoice form's appointment field.
              The service appointment is often already completed (or not a fitting
              type) by the time the quote is converted. */}
          <AppointmentFormField
            name="appointment"
            label="Appointment"
            control={control}
            patientId={patientId ? patientId.toString() : ""}
            showStatus
            onAppointmentSelected={onAppointmentSelected}
          />

          <DateFormField
            name="serviceDate"
            label="Service Date"
            control={control}
            sx={{ m: 0 }}
          />
          {requiresServiceDate && (
            <Typography variant="caption" color="error">
              Service date is required unless converting to Draft status.
            </Typography>
          )}

          <Divider />

          {/* Not "Devices": supplies are sourced too (SN-19383), and services and
              fees never appear here, so this is neither "devices" nor "all lines". */}
          <Typography variant="subtitle2" fontWeight="bold">
            Items to Source
          </Typography>

          {isSaleItemsLoading && <CircularProgress size={24} />}

          {!isSaleItemsLoading && convertibleItems.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nothing on this quote needs sourcing — it will be converted as is.
            </Typography>
          )}

          <ProductCatalogFieldProvider>
            {convertibleItems.map((item) => {
              const masterType = Number(item.product?.master_product?.type);
              const masterSubtype = item.product?.master_product?.subtype;
              const stockSelection = getStockSelection(item);
              const excluded = isExcluded(item);
              // A hint, never a rule: `managed_stock` is unreliable (plenty of products
              // have it unset by accident), so it only marks likely candidates. The
              // toggle always defaults to off — nothing is skipped without a click.
              const notStockTracked = item.product?.managed_stock === false;
              return (
                <Box
                  key={item.id}
                  sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1.5 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {item.description || item.product?.display_name}
                      {item.quantity > 1 && ` × ${item.quantity}`}
                      {getItemEar(item) && ` (${getItemEar(item)})`}
                      {notStockTracked && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1, fontWeight: 400 }}
                        >
                          Not stock-tracked
                        </Typography>
                      )}
                    </Typography>
                    {/* Hidden once a unit is picked: there is nothing to exclude from
                        — the item is being sold off the shelf, not ordered. Clearing
                        the stock selection brings the checkbox back. */}
                    {!stockSelection && (
                      <FormControlLabel
                        sx={{ mr: 0, whiteSpace: "nowrap" }}
                        control={
                          <Checkbox
                            size="small"
                            checked={excluded}
                            onChange={(event) =>
                              handleExcludeChange(item, event.target.checked)
                            }
                            inputProps={
                              {
                                "data-testid": `exclude-${item.id}`
                              } as any
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Exclude from order
                          </Typography>
                        }
                      />
                    )}
                  </Stack>
                  <Box sx={{ mb: 1.5 }}>
                    <InventoryProductPills
                      meta={getQuotedDevicePillMeta(item)}
                    />
                  </Box>
                  {/* Hidden rather than disabled while excluded: ProductCatalogField
                      owns its own stock dropdown state, and leaving it mounted invites
                      a selection that contradicts the exclusion. */}
                  {!excluded && (
                    <ProductCatalogField
                      control={control}
                      name={getItemFieldName(item) as any}
                      label={PRODUCT_TYPES.getFromCode(masterType) || "Item"}
                      type={masterType}
                      subtype={
                        masterSubtype != null
                          ? Number(masterSubtype)
                          : undefined
                      }
                      ear={getItemEar(item)}
                      allowInventoryStock
                      disableProductSearch
                    />
                  )}
                  <Typography
                    variant="caption"
                    color={
                      excluded
                        ? "text.secondary"
                        : stockSelection
                          ? "success.main"
                          : "text.secondary"
                    }
                    sx={{ display: "block", mt: 1 }}
                  >
                    {excluded
                      ? "Excluded — no order and no stock movement. This item still bills."
                      : stockSelection
                        ? `Selling from stock${
                            stockSelection.serial_number
                              ? ` — S/N: ${stockSelection.serial_number}`
                              : ""
                          }`
                        : "No stock unit selected — an order will be placed for this item."}
                  </Typography>
                </Box>
              );
            })}
          </ProductCatalogFieldProvider>
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={isConverting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isConverting || isSaleItemsLoading || requiresServiceDate}
          >
            {isConverting ? "Converting…" : "Convert Quote"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
